import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getDb } from "../../../util/Constants";
import { collection, addDoc, getDocs, query, where } from "firebase/firestore";
import Swal from "sweetalert2";
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "../../../assets/css/questionnaire.css";

export default function AddMiniGame4Modal({ isOpen, toggle, onQuestionAdded }) {
  const [question, setQuestion] = useState("");
  const [allImages, setAllImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const fetchImages = async () => {
      const storage = getStorage();
      const folderRef = ref(storage, "Mini4");
      const res = await listAll(folderRef);
      const urls = await Promise.all(
        res.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          return { name: itemRef.name, url };
        })
      );
      setAllImages(urls);
    };
    fetchImages();
    setQuestion("");
    setSelectedImages([]);
  }, [isOpen]);

  const handleAddImage = (img) => {
    if (!selectedImages.find((i) => i.name === img.name)) {
      setSelectedImages([...selectedImages, img]);
    }
  };

  const handleRemoveImage = (img) => {
    setSelectedImages(selectedImages.filter((i) => i.name !== img.name));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(selectedImages);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setSelectedImages(reordered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) {
      Swal.fire("Error", "Question cannot be empty.", "error");
      return;
    }
    if (selectedImages.length === 0) {
      Swal.fire("Error", "Please select at least one option image.", "error");
      return;
    }
    setLoading(true);
    try {
      const db = getDb();
      const qSnap = await getDocs(
        query(collection(db, "questions"), where("miniGame", "==", "Mini 4"))
      );
      let maxQuestionNumber = 0;
      qSnap.forEach((doc) => {
        const qNum = Number(doc.data().questionNumber);
        if (!isNaN(qNum) && qNum > maxQuestionNumber) maxQuestionNumber = qNum;
      });

      await addDoc(collection(db, "questions"), {
        question,
        options: selectedImages.map((img) => img.url),
        miniGame: "Mini 4",
        mini4Options: String(selectedImages.length),
        createdAt: Date.now(),
        questionNumber: maxQuestionNumber + 1,
      });
      Swal.fire("Success", "Question added successfully!", "success");
      onQuestionAdded();
      toggle();
    } catch (error) {
      console.error("Error adding question:", error);
      Swal.fire("Error", "Failed to add question.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl">
      <ModalHeader className="mini4-modal-header"toggle={toggle}>Add New MiniGame 4 Question</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label for="question">Question</Label>
            <Input
              type="textarea"
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Enter your question here"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Available Images</Label>
            <div className="image-grid">
              {allImages.map((img) => {
                const isSelected = selectedImages.find(
                  (i) => i.name === img.name
                );
                return (
                  <div
                    key={img.name}
                    className={`image-thumb selectable ${
                      isSelected ? "selected" : ""
                    }`}
                    title={img.name}
                  >
                    <img src={img.url} alt={img.name} />
                    <div className="img-label">{img.name}</div>
                    {!isSelected && (
                      <div
                        className="add-badge"
                        onClick={() => handleAddImage(img)}
                        title="Add"
                      >
                        +
                      </div>
                    )}
                    {isSelected && (
                      <div className="add-badge added" title="Already added">
                        ✓
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <small>Select images to add as options. Click the + to add.</small>
          </FormGroup>
          <FormGroup>
            <Label>Selected Images (Drag to reorder, click to remove)</Label>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="selected-images" direction="horizontal">
                {(provided) => (
                  <div
                    className="image-grid selected"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {selectedImages.map((img, idx) => (
                      <Draggable
                        key={img.name}
                        draggableId={img.name}
                        index={idx}
                      >
                        {(provided, snapshot) => (
                          <div
                            className={`image-thumb selected-thumb ${
                              snapshot.isDragging ? "dragging" : ""
                            }`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            title="Drag to reorder. Click to remove."
                            onClick={() => handleRemoveImage(img)}
                          >
                            <img src={img.url} alt={img.name} />
                            <div className="img-label">{img.name}</div>
                            <div className="remove-badge">✕</div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            <small>
              Click an image to remove. Drag and drop to change order.
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            type="submit"
            disabled={
              loading || !question.trim() || selectedImages.length === 0
            }
          >
            {loading ? "Adding..." : "Add"}
          </Button>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
